import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {Bindings} from "data/constants/bindings";
import {DateTime} from "luxon";
import type {IContestStat} from "data/stores/contest_player_stats/contest_player_stats.store";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {TournamentStatus} from "data/enums/torunaments";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType, RosterSelectionType, RosterPlayStatus} from "data/enums";
import type {IRosterStore} from "data/stores/roster/roster.store";
import {IGamePlayer, type IPlayerCalculations} from "data/utils/player.calculations";

interface IParams {
	playerId: number;
	contestId: number;
}

export interface IRoasterPlayerController extends ViewController<IParams> {
	get i18n(): ILocalizationStore;

	get player(): IGamePlayer | undefined;

	get stats(): IContestStat | undefined;

	get opponentSquadName(): string;

	get tournamentDate(): string;

	get versus(): string;

	get hasAction(): boolean;

	get isLive(): boolean;

	get isComplete(): boolean;

	get rosterStatus(): RosterPlayStatus;

	get isNonSchedule(): boolean;

	get isAutoSub(): boolean;

	showStatsModal: () => void;
}

@injectable()
export class RoasterPlayerController implements IRoasterPlayerController {
	@observable private _playerId: number = Number.MAX_SAFE_INTEGER;
	@observable private _contestId: number = 0;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.PlayerCalculations)
		private readonly _playerCalculations: IPlayerCalculations,
		@inject(Bindings.RosterStore) private readonly _rosterStore: IRosterStore,
		@inject(Bindings.TournamentsStore) private _tournamentsStore: ITournamentsStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get player() {
		if (!this._contestId) {
			return;
		}
		return this._playerCalculations.getGamePlayerById(this._playerId, this._contestId);
	}

	get stats() {
		return this.player?.contestStats;
	}

	get opponentSquadName(): string {
		return this.player?.opponent?.abbreviation || "";
	}

	get hasAction() {
		if (!this.stats?.action || !this.isLive) {
			return false;
		}
		return this.stats.action !== "none";
	}

	get isLive(): boolean {
		return Boolean(this.player?.contestStats?.isLocked) && this.isTournamentLive;
	}

	get isComplete(): boolean {
		return this.isTournamentComplete;
	}

	get rosterStatus() {
		if (this.isLive) {
			return RosterPlayStatus.Live;
		}
		if (this.isComplete) {
			return RosterPlayStatus.Complete;
		}

		return RosterPlayStatus.Open;
	}

	get isNonSchedule() {
		return this.isLive || this.isComplete;
	}

	// 3:00PM EST
	get tournamentDate(): string {
		if (!this.tournament?.startDate) {
			return "";
		}
		const dateTime = DateTime.fromISO(this.tournament?.startDate);
		if (!dateTime.isValid) {
			return "";
		}
		return dateTime.toFormat("h:mm a ZZZZ");
	}

	get versus(): string {
		const squadId = this.player?.squadId;
		const homeVersus = this.i18n.t("game.versus.home", "VS");
		const awayVersus = this.i18n.t("game.versus.away", "@");
		return squadId === this.tournament?.homeSquadId ? homeVersus : awayVersus;
	}

	private get isTournamentLive(): boolean {
		return this.tournament?.status === TournamentStatus.PLAYING;
	}

	private get isTournamentComplete(): boolean {
		return this.tournament?.status === TournamentStatus.COMPLETE;
	}

	private get tournament() {
		if (!this.stats?.tournamentId) {
			return;
		}
		return this._tournamentsStore.getTournamentById(this.stats?.tournamentId);
	}

	get isAutoSub(): boolean {
		const rosterPlayer = this._rosterStore.rosterPlayers.find(
			(it) => it.playerId === this._playerId
		);
		return rosterPlayer?.selectionType === RosterSelectionType.AUTO_SUBBED;
	}

	dispose(): void {
		return;
	}

	init(param: IParams): void {
		this._playerId = param.playerId;
		this._contestId = param.contestId;
	}

	onChange(param: IParams): void {
		this._playerId = param.playerId;
		this._contestId = param.contestId;
	}

	showStatsModal = () => {
		if (this.isTournamentComplete || this.isTournamentLive) {
			this._modalsStore.showModal(ModalType.PLAYER_STATS, {
				meta: {
					type: "id",
					id: this._playerId,
					contestId: this._contestId,
				},
			});
		}
	};
}
